/* The admin, as seven modules. Each answers one question:

     Today           what needs me right now
     Numbers         how are we doing
     People          who is on Sift
     Trust & Safety  is anything unsafe or out of compliance
     Inbox           who is asking us something
     Marketing       how do we get found
     Money           where the money is

   It grew one top-level page per project (Vinnie, Weekly KPIs, Clickstream,
   Growth, Cream Label, Labor Index, Privacy, ...) and one tab per queue on the
   old Platform page (16 of them), so the same question had three homes and a
   new queue meant hunting for it. Every section below is an existing screen;
   this file is only where each one lives now. The old URLs redirect
   (LEGACY_REDIRECTS), so links in emails and bookmarks still land. */
import {
  Inbox, BarChart3, Users, ShieldCheck, LifeBuoy, Megaphone, DollarSign, Sun,
} from "lucide-react";

export const MODULES = [
  { key: "today", label: "Today", Icon: Sun, to: "/admin", sections: [] },
  {
    key: "numbers", label: "Numbers", Icon: BarChart3,
    sections: [
      // A range you pick, money included. Was "Vinnie".
      { key: "scoreboard", label: "Scoreboard" },
      // Monday weeks, counted from records. Was "Weekly KPIs".
      { key: "weekly", label: "By week" },
      // Signup funnels, company journey, split tests. Was "Growth".
      { key: "funnels", label: "Funnels & tests" },
      // Where visitors come from and what they click. Was "Clickstream".
      { key: "traffic", label: "Traffic" },
      // What companies typed into the opening question. Review weekly.
      { key: "asks", label: "What people ask for" },
    ],
  },
  {
    key: "people", label: "People", Icon: Users,
    sections: [
      { key: "users", label: "Users" },
      // Who is available right now, bucketed by field.
      { key: "talent", label: "Talent" },
      { key: "companies", label: "Companies" },
      // Sift's own agreements with companies, across all of them. Countersign here.
      { key: "agreements", label: "Agreements" },
      { key: "postings", label: "Posts" },
      { key: "partners", label: "Partners" },
      { key: "activity", label: "Recent activity" },
    ],
  },
  {
    key: "trust", label: "Trust & Safety", Icon: ShieldCheck,
    sections: [
      { key: "moderation", label: "Post approval" },
      { key: "reports", label: "Reports" },
      { key: "fraud", label: "Fraud review" },
      { key: "ratings", label: "Disputed ratings" },
      { key: "credential-checks", label: "Credential checks" },
      { key: "credential-disputes", label: "Credential disputes" },
      { key: "adverse-actions", label: "Adverse actions" },
      { key: "dmca", label: "DMCA" },
      { key: "privacy", label: "Privacy requests" },
    ],
  },
  {
    key: "inbox", label: "Inbox", Icon: LifeBuoy,
    sections: [
      { key: "support", label: "Support" },
      { key: "answers", label: "Saved answers" },
      { key: "team", label: "Support team" },
      { key: "contact", label: "Contact form" },
    ],
  },
  {
    key: "marketing", label: "Marketing", Icon: Megaphone,
    sections: [
      { key: "pipeline", label: "Pipeline" },
      { key: "prospects", label: "Prospects" },
      { key: "tasks", label: "Tasks" },
      // Jake's five cold calls a day: Indiana agencies, numbers, script.
      { key: "calls", label: "Calls" },
      // Jake's monthly letter to everyone on Sift; drafts wait here for approval.
      { key: "member-update", label: "Monthly update" },
      { key: "linkedin", label: "LinkedIn posts" },
      { key: "planned", label: "Planned posts" },
      { key: "spotlight", label: "Talent spotlight" },
      { key: "hiring", label: "Hiring features" },
      { key: "metrics", label: "Weekly metrics" },
    ],
  },
  {
    key: "money", label: "Money", Icon: DollarSign,
    sections: [
      { key: "overview", label: "Overview" },
      { key: "pnl", label: "P&L" },
      { key: "revenue", label: "Revenue" },
      { key: "expenses", label: "Expenses" },
      { key: "burn", label: "Burn & runway" },
      // GMV, take, users: the old Platform "Overview" tab.
      { key: "volume", label: "Platform volume" },
      { key: "projections", label: "Projections" },
      { key: "tax", label: "1099 / Tax" },
      { key: "labor-index", label: "Labor Index" },
    ],
  },
];

export const sectionPath = (moduleKey, sectionKey) => `/admin/${moduleKey}/${sectionKey}`;

export function findSection(pathname) {
  const [, , m, s] = (pathname || "").split("/");
  const mod = MODULES.find((x) => x.key === m) || (pathname === "/admin" || pathname === "/admin/" ? MODULES[0] : null);
  const section = mod?.sections.find((x) => x.key === s) || null;
  return { mod, section };
}

/* Old address -> new one. Marketing's ?tab= links (the hiring-feature alert
   email uses one) map onto the section of the same name. */
export const LEGACY_REDIRECTS = {
  "/admin/dashboard": "/admin",
  "/admin/finance": "/admin/money/overview",
  "/admin/partners": "/admin/people/partners",
  "/admin/labor-index": "/admin/money/labor-index",
  "/admin/growth-funnel": "/admin/numbers/funnels",
  "/admin/clickstream": "/admin/numbers/traffic",
  "/admin/kpis": "/admin/numbers/weekly",
  "/admin/vinnie": "/admin/numbers/scoreboard",
  "/admin/support": "/admin/inbox/support",
  "/admin/privacy-requests": "/admin/trust/privacy",
};

// Section key -> the tab key the existing screen already uses internally.
export const PLATFORM_TAB = {
  users: "users", companies: "organizations", postings: "postings", activity: "activity",
  moderation: "moderation", reports: "reports", fraud: "fraud", ratings: "disputes",
  "credential-checks": "credverify", "credential-disputes": "creddisputes",
  "adverse-actions": "adverseaction", dmca: "dmca", contact: "contact",
  volume: "overview", projections: "projections", tax: "tax",
};
export const MARKETING_TAB = { pipeline: "dashboard" };
